import { compileIntake, INTAKE_WRITE_SECTIONS, mergeInjuries, } from "@repo/contract/training";
import { v7 } from "uuid";
import { ApiError } from "../api/api-error";
import { fetchInjuries, putEquipment, putInjuries, putParq, putProfile, putTrainingConfig, recordMeasurement, } from "../data/profile";
/**
 * The intake's one write (`D126`) — compile everything once, write everything
 * once, in order. Ported from the Expo build at `expo-app-final`; the mapping
 * itself is the contract's `compileIntake` (plan D52), and this file is only
 * the order, the stopping and the retry.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ── ORDER, AND WHY `trainingConfig` IS LAST ────────────────────────────────
 * {@link COMMIT_STEPS} is the contract's `INTAKE_WRITE_SECTIONS`. The server's
 * progress completes eight of the sixteen steps the moment the
 * `training_config` row lands, and the plan proposer refuses until that row
 * exists — so the row means *"the intake is done"*. Written last, a commit
 * that dies half-way leaves it absent and every reader says *"not finished"*,
 * which is true.
 *
 * ── WHERE THE PERSON GOES NEXT IS NOT DECIDED HERE ─────────────────────────
 * The Expo build returned the route `/plan/review`. The screen decides the
 * handoff (T52), and the plan review screen generates its own draft
 * (`D150`, `T-145`): a caller that generated before navigating would have its
 * draft replaced.
 * ═══════════════════════════════════════════════════════════════════════════
 */
/** The ordered steps of the commit — the contract's write order. */
export const COMMIT_STEPS = INTAKE_WRITE_SECTIONS;
/** A key for one commit's weigh-in: a UUIDv7 under a prefix, within the
 *  contract's 64-character bound. */
export function newIdempotencyKey() {
    return `intake-${v7()}`;
}
/**
 * Compile once, then write in order.
 *
 * When a write fails it STOPS: the outcome is `partial`, names the step and
 * carries the error; nothing after it is attempted (a config written over a
 * failed PAR-Q would announce a finished intake missing a safety
 * questionnaire); and a retry skips exactly what `alreadyWritten` names.
 *
 * `compileIntake` throws `IntakeCompileError` for an incomplete set — a
 * programming error, before anything is sent.
 */
export async function commit(deps, answers) {
    const plan = compileIntake(answers);
    const done = COMMIT_STEPS.filter((step) => deps.alreadyWritten?.includes(step));
    for (const step of COMMIT_STEPS) {
        if (done.includes(step)) {
            continue;
        }
        try {
            await write(step, plan, deps);
        }
        catch (error) {
            return { done, error, failedAt: step, kind: "partial" };
        }
        done.push(step);
    }
    return { done, kind: "committed" };
}
/** One step's route call. A `switch`, so a step added to the contract's order
 *  without a branch here does not compile. */
function write(step, plan, deps) {
    const { api } = deps;
    switch (step) {
        case "profile":
            return putProfile(api, plan.profile);
        case "measurement":
            return recordWeight(api, plan.weight, deps.idempotencyKey);
        case "parq":
            return putParq(api, plan.parq);
        case "injuries":
            return writeInjuries(api, plan.currentPain);
        case "equipment":
            return putEquipment(api, plan.equipment);
        case "trainingConfig":
            return putTrainingConfig(api, plan.trainingConfig);
        default:
            return unreachable(step);
    }
}
function unreachable(step) {
    throw new Error(`the commit has no write for ${String(step)}`);
}
/**
 * The weigh-in, with its key, and once more without it if the server refuses
 * the BODY — an API deployed before `D154` whose `z.strictObject` does not
 * know the key would otherwise fail the intake on every attempt. The retry
 * cannot write twice: a body the schema refused stored nothing.
 */
async function recordWeight(api, weight, idempotencyKey) {
    try {
        return await recordMeasurement(api, { ...weight, idempotencyKey });
    }
    catch (error) {
        if (!refusedTheBody(error)) {
            throw error;
        }
        return await recordMeasurement(api, weight);
    }
}
/**
 * The server refused the body rather than a value in it: `parseBody` throws
 * `request.invalid_body` with `params.field` from the first zod issue's path,
 * and an unrecognized key is a ROOT issue with an empty path — so the field is
 * absent. `measurementInput` has no root refinement, so on this route that pair
 * has one cause. Narrow rather than certain; the cost of being wrong is one
 * more refused request.
 */
function refusedTheBody(error) {
    return (error instanceof ApiError &&
        error.code === "VALIDATION" &&
        error.messageKey === "request.invalid_body" &&
        error.params?.field === undefined);
}
/**
 * The merge that makes the injuries step safe (`D155`): the stored list is
 * read at the moment of the write — never earlier, or a past injury recorded
 * while the intake sat half-answered would be put back the way it was — and
 * the intake's current reports go OVER its historical rows
 * (`mergeInjuries`). A failed read fails the step: guessing an empty stored
 * list is the deletion this step exists to prevent.
 */
async function writeInjuries(api, currentPain) {
    const stored = await fetchInjuries(api);
    return putInjuries(api, mergeInjuries(currentPain, stored.injuries));
}
//# sourceMappingURL=commit.js.map